/**
 * FeedbackWidget.tsx
 *
 * Session-level Likert scale feedback overlay for the PepWise Conversational Agent.
 *
 * Acceptance criteria covered
 * ─────────────────────────────
 * AC2  Non-blocking slide-up panel; Skip always visible
 * AC3  1–5 Likert scale with star buttons + extreme labels
 * AC4  Optional comment box for ratings 4–5
 * AC5  Mandatory comment box for ratings 1–3; Submit disabled until ≥ 1 char
 * AC6  Comment box transitions dynamically on rating change; text preserved
 * AC7  Submit → thank-you → auto-close; error → retry + "Skip and exit"
 * AC8  Skip → no record written, user exits immediately
 * AC10 Enforced via sessionStorage guard (keyed to session_id) — widget never
 *      re-renders for the same session after Submit or Skip
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { skipSessionFeedback, submitSessionFeedback } from '../../service/service';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface FeedbackWidgetProps {
  sessionId: string;
  /** Called after the user submits or skips — parent should close the chat. */
  onDone: () => void;
  /** Optional: pre-fetched query context to include in the feedback payload */
  queryCount?: number;
  queryTypes?: string[];
}

type WidgetState = 'idle' | 'submitting' | 'success' | 'error';

// ─────────────────────────────────────────────────────────────────────────────
// Session guard helpers
// ─────────────────────────────────────────────────────────────────────────────

const feedbackDoneKey = (sessionId: string) => `pepwise_feedback_done_${sessionId}`;

export function markFeedbackDone(sessionId: string): void {
  try {
    sessionStorage.setItem(feedbackDoneKey(sessionId), '1');
  } catch {
    /* quota exceeded — non-fatal */
  }
}

export function isFeedbackDone(sessionId: string): boolean {
  try {
    return !!sessionStorage.getItem(feedbackDoneKey(sessionId));
  } catch {
    return false;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Star Rating sub-component
// ─────────────────────────────────────────────────────────────────────────────

const StarRating: React.FC<{
  value: number | null;
  onChange: (v: number) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = (hovered ?? value ?? 0) >= star;
        return (
          <Box
            key={star}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: 34,
            }}
          >
            <IconButton
              aria-label={`Rate ${star} out of 5`}
              disabled={disabled}
              onMouseEnter={() => !disabled && setHovered(star)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => !disabled && onChange(star)}
              disableRipple
              sx={{
                p: 0.25,
                color: filled ? '#F59E0B' : '#CBD5E1',
                transition: 'color 0.15s, transform 0.1s',
                transform: filled ? 'scale(1.15)' : 'scale(1)',
                '&:hover': { backgroundColor: 'transparent' },
              }}
            >
              {filled ? (
                <StarIcon sx={{ fontSize: 28 }} />
              ) : (
                <StarBorderIcon sx={{ fontSize: 28 }} />
              )}
            </IconButton>
            <Typography
              sx={{
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontSize: '10px',
                color: value === star ? '#003771' : '#94A3B8',
                fontWeight: value === star ? 700 : 400,
                transition: 'color 0.15s',
                lineHeight: 1,
                mt: 0.25,
              }}
            >
              {star}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({
  sessionId,
  onDone,
  queryCount = 0,
  queryTypes = [],
}) => {
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [widgetState, setWidgetState] = useState<WidgetState>('idle');
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
    };
  }, []);

  // ── Derived state ──────────────────────────────────────────────────────────

  const isLowRating = rating !== null && rating <= 3;
  const isCommentRequired = isLowRating;
  const isSubmitEnabled =
    rating !== null &&
    widgetState === 'idle' &&
    (!isCommentRequired || comment.trim().length > 0);

  const commentLabel = isCommentRequired
    ? 'Your comment can help improve the performance of the Conversational Agent.'
    : 'Any additional comments? (Optional)';

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSkip = useCallback(async () => {
    markFeedbackDone(sessionId);
    // Fire-and-forget — skip is best-effort (AC8)
    skipSessionFeedback(sessionId).catch(() => { });
    onDone();
  }, [sessionId, onDone]);

  const handleSubmit = useCallback(async () => {
    if (!isSubmitEnabled || rating === null) return;

    setWidgetState('submitting');

    try {
      await submitSessionFeedback(sessionId, {
        rating,
        comment: comment.trim() || undefined,
        query_count: queryCount,
        query_types: queryTypes,
      });

      markFeedbackDone(sessionId);
      setWidgetState('success');

      // AC7 — brief confirmation before auto-close
      autoCloseTimer.current = setTimeout(() => {
        onDone();
      }, 1800);
    } catch {
      setWidgetState('error');
    }
  }, [isSubmitEnabled, rating, comment, sessionId, queryCount, queryTypes, onDone]);

  const handleRetry = useCallback(() => {
    setWidgetState('idle');
  }, []);

  const handleSkipAndExit = useCallback(() => {
    // AC7 — "Skip and exit" on submission failure — never traps the user
    markFeedbackDone(sessionId);
    onDone();
  }, [sessionId, onDone]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const isDisabled = widgetState === 'submitting' || widgetState === 'success';

  return (
    <Box
      role="dialog"
      aria-label="Session feedback"
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
        borderTop: '2px solid #003771',
        borderRadius: '12px 12px 0 0',
        boxShadow: '0 -8px 32px rgba(0, 55, 113, 0.12)',
        p: 2.5,
        animation: 'feedbackSlideUp 0.28s cubic-bezier(0.34, 1.2, 0.64, 1)',
        '@keyframes feedbackSlideUp': {
          from: { transform: 'translateY(100%)', opacity: 0 },
          to: { transform: 'translateY(0)', opacity: 1 },
        },
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 1.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              fontSize: '15px',
              fontWeight: 700,
              color: '#0F172A',
              lineHeight: 1.3,
            }}
          >
            How would you rate your experience in this session?
          </Typography>
        </Box>

        {/* Skip link — always visible (AC2) */}
        {widgetState !== 'success' && (
          <Tooltip title="Skip feedback">
            <IconButton
              size="small"
              onClick={handleSkip}
              disabled={widgetState === 'submitting'}
              sx={{
                color: '#94A3B8',
                ml: 1,
                flexShrink: 0,
                '&:hover': { color: '#003771', backgroundColor: 'rgba(0,55,113,0.06)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* ── Success state ────────────────────────────────────────────────── */}
      {widgetState === 'success' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1.5,
            px: 2,
            borderRadius: '10px',
            backgroundColor: '#ECFDF5',
            border: '1px solid #A7F3D0',
          }}
        >
          {/* Animated checkmark */}
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              backgroundColor: '#10B981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              animation: 'feedbackPop 0.3s ease-out',
              '@keyframes feedbackPop': {
                '0%': { transform: 'scale(0.5)', opacity: 0 },
                '70%': { transform: 'scale(1.15)' },
                '100%': { transform: 'scale(1)', opacity: 1 },
              },
            }}
          >
            <Box
              component="span"
              sx={{ color: '#fff', fontSize: '16px', fontWeight: 700, lineHeight: 1 }}
            >
              ✓
            </Box>
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                color: '#065F46',
              }}
            >
              Thank you for your feedback!
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontSize: '12px',
                color: '#047857',
                mt: 0.25,
              }}
            >
              Your response helps us improve the Conversational Agent.
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Error state ──────────────────────────────────────────────────── */}
      {widgetState === 'error' && (
        <Box
          sx={{
            py: 1.25,
            px: 2,
            mb: 1.5,
            borderRadius: '8px',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              fontSize: '13px',
              color: '#DC2626',
              fontWeight: 500,
            }}
          >
            Feedback could not be submitted. Please try again.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Box
              component="button"
              onClick={handleRetry}
              sx={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 700,
                color: '#DC2626',
                textDecoration: 'underline',
                p: 0,
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                '&:hover': { color: '#B91C1C' },
              }}
            >
              Retry
            </Box>
            <Box
              component="button"
              onClick={handleSkipAndExit}
              sx={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 500,
                color: '#64748B',
                textDecoration: 'underline',
                p: 0,
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                '&:hover': { color: '#334155' },
              }}
            >
              Skip and exit
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Rating + comment form (hidden in success state) ─────────────── */}
      {widgetState !== 'success' && (
        <>
          {/* Star rating (AC3) */}
          <Box sx={{ mb: 2 }}>
            <StarRating value={rating} onChange={setRating} disabled={isDisabled} />

            {/* Extreme labels */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  fontSize: '11px',
                  fontWeight: 500,
                  color: '#94A3B8',
                }}
              >
                Not helpful
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  fontSize: '11px',
                  fontWeight: 500,
                  color: '#94A3B8',
                }}
              >
                Very helpful
              </Typography>
            </Box>
          </Box>

          {/* Dynamic comment box (AC4, AC5, AC6) */}
          {rating !== null && (
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  fontSize: '12px',
                  fontWeight: isCommentRequired ? 700 : 500,
                  color: isCommentRequired ? '#DC2626' : '#64748B',
                  mb: 0.75,
                  transition: 'color 0.2s, font-weight 0.2s',
                }}
              >
                {commentLabel}
                {isCommentRequired && (
                  <Box component="span" sx={{ color: '#DC2626', ml: 0.5 }}>
                    *
                  </Box>
                )}
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                value={comment}
                onChange={(e) => {
                  if (e.target.value.length <= 500) setComment(e.target.value);
                }}
                disabled={isDisabled}
                placeholder={
                  isCommentRequired
                    ? 'Please share what went wrong...'
                    : 'Share any additional thoughts...'
                }
                variant="outlined"
                size="small"
                autoComplete="off"
                inputProps={{ maxLength: 500, 'aria-required': isCommentRequired }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    fontSize: '13px',
                    borderRadius: '8px',
                    backgroundColor: '#FFFFFF',
                    transition: 'border-color 0.2s',
                    '& fieldset': {
                      borderColor: isCommentRequired ? '#FCA5A5' : '#E2E8F0',
                      borderWidth: isCommentRequired ? '1.5px' : '1px',
                    },
                    '&:hover fieldset': {
                      borderColor: isCommentRequired ? '#EF4444' : '#003771',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: isCommentRequired ? '#DC2626' : '#003771',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#0F172A !important',
                    WebkitTextFillColor: '#0F172A !important',
                    caretColor: '#0F172A',
                  },
                }}
              />
              <Typography
                sx={{
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  fontSize: '10px',
                  color: comment.length > 450 ? '#EF4444' : '#94A3B8',
                  textAlign: 'right',
                  mt: 0.5,
                  transition: 'color 0.2s',
                }}
              >
                {comment.length} / 500
              </Typography>
            </Box>
          )}

          {/* Action row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1.5,
            }}
          >
            {/* Submit button (AC5 — disabled until comment present for low ratings) */}
            <Box
              component="button"
              onClick={handleSubmit}
              disabled={!isSubmitEnabled}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 2.5,
                py: 1,
                borderRadius: '8px',
                border: 'none',
                backgroundColor: isSubmitEnabled ? '#003771' : '#CBD5E1',
                color: '#FFFFFF',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontSize: '13px',
                fontWeight: 700,
                cursor: isSubmitEnabled ? 'pointer' : 'not-allowed',
                transition: 'background-color 0.2s, transform 0.1s',
                '&:not(:disabled):hover': {
                  backgroundColor: '#002B59',
                  transform: 'translateY(-1px)',
                },
                '&:not(:disabled):active': { transform: 'translateY(0)' },
              }}
            >
              {widgetState === 'submitting' ? (
                <>
                  <CircularProgress size={14} sx={{ color: '#fff' }} />
                  Submitting…
                </>
              ) : (
                'Submit'
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default FeedbackWidget;
